import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript/";
import { CheckoutService, AmountDto } from "../api";
import { StripeElements, StripeExpressCheckoutElement, StripeExpressCheckoutElementClickEvent, StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementOptions, StripeExpressCheckoutElementReadyEvent, StripeExpressCheckoutElementShippingAddressChangeEvent, StripeExpressCheckoutElementShippingRateChangeEvent } from "@stripe/stripe-js";
import { getCartStore, getOrderStore, getStripeStore } from "../utils";
import { mapLineItemsForStripe, mapShippingMethodForStripe } from "./stripe-utils";
import { ProductContextComponent } from "../product-context.component";

// This component handles both express checkout and buy now options
export class ExpressCheckoutComponent extends AlpineComponent {
    private stripeElements?: StripeElements | null = null;
    private stripeExpressCheckoutElement?: StripeExpressCheckoutElement | null = null;
    private productContextComponent?: ProductContextComponent | null = null;
    private mode: "expressCheckout" | "buyNow" = "expressCheckout";

    public expressCheckoutAvailable: boolean = true;
    public expressCheckoutReady: boolean = false;
    public expressCheckoutMounted: boolean = false;

    constructor() {
        super();

        const orderStore = getOrderStore();

        if(orderStore.transactionAmount?.value === 0) {
            this.expressCheckoutAvailable = false;
        }
    }

    init() {
        this.productContextComponent = this.$data  as unknown as ProductContextComponent;
        if(!this.productContextComponent.addToCart) {
            this.productContextComponent = undefined;
            this.mode = "expressCheckout";
        } else {
            this.mode = "buyNow";
        }

        window.Alpine.effect(() => {
            //It appears the stripe messaing element does not support updates on unmounting.
            let transactionAmount: AmountDto = { value: 0, formatted: { value: "0.00" } };
            if(this.mode === "buyNow") {
                transactionAmount = this.getProductTotalPrice() ?? { value: 0, formatted: { value: "0.00" } };
            } else {
                const orderStore = getOrderStore();
                transactionAmount = orderStore.transactionAmount ?? { value: 0, formatted: { value: "0.00" } };
            }
            this.handleTransactionAmountUpdated(transactionAmount);
        });

        this.$watch("$store.order.transactionAmount", (newValue) => {
            if((newValue as AmountDto)?.value === 0) {
                if(this.stripeExpressCheckoutElement){
                    this.stripeExpressCheckoutElement.unmount();
                    this.expressCheckoutAvailable = false;
                    this.expressCheckoutMounted = false;
                }
            } else {
                if(this.stripeExpressCheckoutElement && this.expressCheckoutMounted === false){
                    this.mount();
                }
            }
        });

        this.$watch("$store.stripe.stripeReady", (newValue) => {
            if(newValue === true) {
                this.setupElement();
                this.mount();
            }
        });
    }

    setupElement(){
        const orderStore = getOrderStore();
        const stripeStore = getStripeStore();
        this.stripeElements = stripeStore.getStripeElements();
        if(stripeStore.stripeReady && this.stripeElements) {
            this.stripeExpressCheckoutElement = this.stripeElements.create(
                'expressCheckout',
                {
                    allowedShippingCountries: stripeStore.allowedCountries,
                    paymentMethods:{
                        amazonPay: 'never',
                        applePay: 'always',
                        googlePay: 'always',
                        link: 'auto',
                        klarna: 'auto',
                    },
                    billingAddressRequired: true,
                    emailRequired: true,
                    phoneNumberRequired: true,
                    shippingAddressRequired: true,
                    shippingRates: mapShippingMethodForStripe(orderStore.allowedShippingMethods),
                    lineItems: mapLineItemsForStripe(),
                    buttonType: this.mode === "buyNow" ? { applePay: 'buy', googlePay: 'buy', paypal: 'buynow', klarna: 'continue', } : {}
                } as StripeExpressCheckoutElementOptions);
            this.stripeExpressCheckoutElement.on('ready', this.handleStripeExpressCheckoutReady.bind(this));
            this.stripeExpressCheckoutElement.on('click', this.handleStripeExpressCheckoutClick.bind(this));
            this.stripeExpressCheckoutElement.on('shippingaddresschange', this.handleStripeShippingAddressChange.bind(this));
            this.stripeExpressCheckoutElement.on('shippingratechange', this.handleStripeShippingRateChange.bind(this));
            this.stripeExpressCheckoutElement.on('confirm', this.handleStripeExpressCheckoutConfirm.bind(this));
        }
    }

    mount() {
        if(this.stripeExpressCheckoutElement && getStripeStore().stripeReady && !this.expressCheckoutMounted) {
            this.stripeExpressCheckoutElement.mount(this.$refs["stripe-express-checkout-container"]);
            this.expressCheckoutMounted = true;
        }
    }

    async handleStripeExpressCheckoutReady(event: StripeExpressCheckoutElementReadyEvent){
        this.expressCheckoutReady = true;

        this.expressCheckoutAvailable = event.availablePaymentMethods ? true : false;
    }

    async handleStripeExpressCheckoutClick(event: StripeExpressCheckoutElementClickEvent) {
        if(!this.productContextComponent) {
            event.resolve();
            return;
        }
        // Buy Now with express checkout
        const cartStore = getCartStore();
        await cartStore.ClearCart(true);
        await this.productContextComponent.addToCart(true);
        event.resolve();
    }

    async handleStripeShippingAddressChange(event: StripeExpressCheckoutElementShippingAddressChangeEvent){
        console.debug("Handling Stripe shipping address change:", event);

        const orderStore = getOrderStore();

        const { customerInformation, shippingInformation, billingInformation } = orderStore;

        customerInformation.firstName = event.name?.split(" ")[0] ?? customerInformation.firstName;
        customerInformation.lastName = event.name?.split(" ").slice(1).join(" ") ?? customerInformation.lastName;
        shippingInformation.isSameAsBilling = true;
        billingInformation.firstName = event.name?.split(" ")[0] ?? customerInformation.firstName;
        billingInformation.lastName = event.name?.split(" ").slice(1).join(" ") ?? customerInformation.lastName;
        billingInformation.city = event.address?.city;
        billingInformation.postcode = event.address?.postal_code;

        try
        {
            // We can't validate here as the address will be incomplete, but we need to update to get shipping methods
            await orderStore.updateCustomerInformation({
                customerInformation,
                shippingInformation,
                billingInformation
            }, { policy: 'immediate', priority: 'high', validate: false, throwOnInvalid: false });
        }
        catch(error){
            event.reject();
            return;
        }

        console.debug("Allowed shipping methods after address change:", orderStore.allowedShippingMethods);

        if(orderStore.allowedShippingMethods.length === 0){
            event.reject();
            return;
        }

        if(orderStore.shippingMethodId !== orderStore.allowedShippingMethods[0].id){
            orderStore.shippingMethodId = orderStore.allowedShippingMethods[0].id;
        }

        event.resolve({
            shippingRates: mapShippingMethodForStripe(orderStore.allowedShippingMethods)
        });
    }

    handleStripeShippingRateChange(event: StripeExpressCheckoutElementShippingRateChangeEvent){
        console.debug("Handling Stripe shipping rate change:", event);

        const orderStore = getOrderStore();
        orderStore.shippingMethodId = event.shippingRate.id;

        // Reactive events would have happened after assignment above
        if(orderStore.shippingMethodId !== event.shippingRate.id){
            console.error("Selected shipping method ID does not match event shipping rate ID");
            event.reject();
            return;
        }

        event.resolve({
            shippingRates: mapShippingMethodForStripe(orderStore.allowedShippingMethods),
        });
    }
    handleTransactionAmountUpdated(newValue: AmountDto) {
        if(this.mode === "buyNow") {
            const stripeStore = getStripeStore();
            stripeStore.updateTransactionAmount(newValue);
        }
        // Stripe store handles order store updates.

        if(newValue.value === 0) {
            if(this.stripeExpressCheckoutElement){
                this.stripeExpressCheckoutElement.unmount();
                this.expressCheckoutAvailable = false;
                this.expressCheckoutMounted = false;
            }
        } else {
            if(this.stripeExpressCheckoutElement && this.expressCheckoutMounted === false){
                this.mount();
                this.expressCheckoutAvailable = true;
                this.expressCheckoutMounted = true;
            }
        }
    }

    async handleStripeExpressCheckoutConfirm(event: StripeExpressCheckoutElementConfirmEvent){
        console.debug("Handling Stripe confirm event:", event);

        const orderStore = getOrderStore();
        const stripeStore = getStripeStore();

        if(!stripeStore.stripeReady || !this.stripeElements){
            console.error("Stripe is not ready in confirm handler");
            return;
        }

        const transactionAmountBefore = orderStore.transactionAmount?.value;

        const customerInformation = orderStore.customerInformation;
        customerInformation.firstName = event.billingDetails?.name ? event.billingDetails?.name?.split(" ")[0] ?? "" : event.shippingAddress?.name?.split(" ")[0] ?? customerInformation.firstName;
        customerInformation.lastName = event.billingDetails?.name ? event.billingDetails?.name?.split(" ").slice(1).join(" ") ?? "" : event.shippingAddress?.name?.split(" ").slice(1).join(" ") ?? customerInformation.lastName;
        customerInformation.email = event.billingDetails?.email ?? customerInformation.email;
        customerInformation.phoneNumber = event.billingDetails?.phone ?? customerInformation.phoneNumber;

        const shippingInformation = orderStore.shippingInformation;

        shippingInformation.firstName = event.shippingAddress?.name?.split(" ")[0] ?? customerInformation.firstName;
        shippingInformation.lastName = event.shippingAddress?.name?.split(" ").slice(1).join(" ") ?? customerInformation.lastName;
        shippingInformation.addressLine1 = event.shippingAddress?.address?.line1 ?? '';
        shippingInformation.addressLine2 = event.shippingAddress?.address?.line2;
        shippingInformation.city = event.shippingAddress?.address?.city;
        shippingInformation.postcode = event.shippingAddress?.address?.postal_code;
        shippingInformation.isSameAsBilling = false;

        const billingInformation = orderStore.billingInformation;

        billingInformation.firstName = customerInformation.firstName;
        billingInformation.lastName = customerInformation.lastName;
        billingInformation.addressLine1 = event.billingDetails ? event.billingDetails.address?.line1 : event.shippingAddress?.address?.line1 ?? '';
        billingInformation.addressLine2 = event.billingDetails ? event.billingDetails.address?.line2 : event.shippingAddress?.address?.line2;
        billingInformation.city = event.billingDetails ? event.billingDetails.address?.city : event.shippingAddress?.address?.city;
        billingInformation.postcode = event.billingDetails ? event.billingDetails.address?.postal_code : event.shippingAddress?.address?.postal_code;
        billingInformation.email = customerInformation.email;
        billingInformation.phoneNumber = customerInformation.phoneNumber;

        try
        {
            await orderStore.updateCustomerInformation({ customerInformation, shippingInformation, billingInformation }, { policy: 'immediate', priority: 'high', validate: true, throwOnInvalid: true });
        }
        catch(customerUpdateError) {
            event.paymentFailed({ reason: "fail", message: "Error updating customer information." });
            if(customerUpdateError) {
                window.appInsights?.trackTrace({ message: "Error updating customer information.", severityLevel: 3, properties: { error: JSON.stringify(customerUpdateError) } });
            }
            return;
        }

        // Handle situation where transaction amount has changed during customer info update.
        if(transactionAmountBefore !== orderStore.transactionAmount?.value){
            event.paymentFailed({ reason: "fail", message: "Transaction amount has changed, please review and try again." });
            return;
        }

        // TODO: Consider if this should be handled by order store.
        const { data: paymentUpdateData, error: paymentUpdateError } = await CheckoutService.postSetPaymentMethod({
            path: { storeId: orderStore.storeId },
            body: {
                paymentMethodId: orderStore.allowedPaymentMethods.find(pm => pm.alias == "stripePayments")?.id!,
            }
        });

        // Handle situation where transaction amount has changed during payment method update.
        if(transactionAmountBefore !== orderStore.transactionAmount?.value){
            event.paymentFailed({ reason: "fail", message: "Transaction amount has changed, please review and try again." });
            return;
        }

        if(paymentUpdateError || !paymentUpdateData){
            console.error("Error setting payment method before confirming Stripe payment:", paymentUpdateError);
            if(paymentUpdateError) {
                window.appInsights?.trackTrace({ message: "Error updating payment method.", severityLevel: 3, properties: { error: JSON.stringify(paymentUpdateError) } });
            }
            return;
        }

        const { error: submitError } = await this.stripeElements!.submit();
        if(submitError){
            console.error("Error submitting Stripe payment elements:", submitError);
            window.appInsights?.trackTrace({ message: "Error submitting stripe elements.", severityLevel: 3, properties: { error: JSON.stringify(submitError) } });
            return;
        }

        const { data, error } = await CheckoutService.postInitPayment({
            path:{
                storeId: orderStore.storeId,
            }
        });

        if(error || !data || !data.parameters || !data.parameters["clientSecret"] || !data.parameters["returnUrl"]){
            console.error("Error initializing Stripe payment on server:", error);
            event.paymentFailed({ reason: "fail", message: "Error initializing payment." });
            if(error) {
                window.appInsights?.trackTrace({ message: "Error initializing Stripe payment on server.", severityLevel: 3, properties: { error: JSON.stringify(error) } });
            }
            return;
        }

        const { error: confirmError } = await stripeStore.stripe!.confirmPayment({
            clientSecret: data.parameters["clientSecret"],
            confirmParams: {
                return_url: data.parameters["returnUrl"],
            },
            elements: this.stripeElements!,
        });

        if(confirmError){
            console.error("Error confirming Stripe payment:", confirmError);
            window.appInsights?.trackTrace({ message: "Error confirming Stripe payment", severityLevel: 3, properties: { error: JSON.stringify(error) } });
        }
    }

    private getProductTotalPrice() : AmountDto | null | undefined {
        const calculatedTotalAmount = this.productContextComponent?.calculatedTotalAmount;
        if(calculatedTotalAmount) {
            return calculatedTotalAmount;
        } else {
            const orderStore = getOrderStore();
            return orderStore.transactionAmount;
        }
    }
}